import {Room, RoomJoin, RoomJoins, Rooms} from "./types";
import React, {ReactElement} from "react";
import {StyledBoxColumn, StyledBoxRow, StyledContainer} from "shared/StyledComponents";
import {
  DIVIDER_BLACK,
  PAGE_FRAGMENT_HALF_WIDTH,
  PAGE_FRAGMENT_WIDTH,
  PD_LG,
  PD_MD,
  PD_XLG,
  PD_XLG2
} from "shared/dimens";
import {Action, ListItemChange, OnListItemsListener, UserProfilePhoto} from "shared/types";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "shared/BaseFragment";
import {BuzzButton, CreateOrJoinPage, HeaderButtons} from "./Components";
import {Button, ButtonBase, Card, Grid, IconButton, Typography, useMediaQuery, useTheme} from "@mui/material";
import {ChevronRightOutlined, MoreVertOutlined} from "@mui/icons-material";
import {BaseApp} from "../shared/BaseApp";
import {UserFragment} from "../shared/UserFragment";
import {getMemberAuth} from "../shared/auth";
import Markdown from "react-markdown";
import {BLACK, PURPLE, RED} from "./styles";
import {App} from "./App";
import {SettingsHelper} from "./SettingsHelper";

type Feature = {
  dark?: boolean,
  flip?: boolean,
  color: string,
  title: string,
  text: string,
  image: string,
}

const FEATURES: Feature[] = [
  {
    dark: true,
    color: PURPLE,
    title: "Join from any device",
    text: "Hop on using your phone or tablet with the Hey Buzz Me! app (available on the App Store and Google Play), or just connect straight from your computer browser — no download required.",
    image: "/assets/marketing/devices.png",
  },
  {
    dark: true,
    flip: true,
    color: RED,
    title: "Make it fun!",
    text: "Watch videos, listen to music, and play games together with Live-Sync. You can also add some fun with effects and emoji reactions!",
    image: "/assets/marketing/livesync.png",
  },
];

function FeatureView(props: { feature: Feature }): ReactElement {
  const feature = props.feature;
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  const textBlock = <StyledBoxColumn style={{}}>
    <Typography variant="h4">
      {feature.title}
    </Typography>
    <Typography component="div">
      <Markdown>
        {feature.text}
      </Markdown>
    </Typography>
  </StyledBoxColumn>;
  const imageBlock =
    <img src={feature.image}
         style={{width: "100%", aspectRatio: 1, border: DIVIDER_BLACK}}/>;
  return <StyledBoxColumn style={{color: feature.dark ? "white" : null, background: feature.color, padding: PD_XLG2}}>
    <StyledContainer size="xlg">
      <Grid container spacing={8}>
        <Grid item xs={12} sm={6}>
          {sm && feature.flip
            ? imageBlock
            : textBlock}
        </Grid>
        <Grid item xs={12} sm={6}>
          {!sm || !feature.flip
            ? imageBlock
            : textBlock}
        </Grid>
      </Grid>
    </StyledContainer>
  </StyledBoxColumn>;
}

export type MainFragmentProps = BaseFragmentProps & {}

type MainFragmentState = BaseFragmentState & {
  rooms: Room[],
}

// const HOW_TO_BUZZ = "Calling as easy as 1, 2, 3:" +
//   "&nbsp;&nbsp;\n" +
//   "1. Enter a nickname." +
//   "&nbsp;&nbsp;\n" +
//   "1. Click on the NEW BUZZ button." +
//   "&nbsp;&nbsp;\n" +
//   "1. Share the link and connect.";


export class MainFragment extends BaseFragment<MainFragmentProps, MainFragmentState> implements OnListItemsListener<RoomJoin> {

  private readonly memberAuth = getMemberAuth();
  private readonly loader = RoomJoins.getInstance();

  protected async fetchOnMount(forceReload?: boolean): Promise<void> {
    if (forceReload) {
      await this.loader.loadListItems();
    } else {
      await this.loader.maybeLoadListItems();
    }
    const rooms: Room[] = [];
    for (const join of this.loader.getListItems()) {
      const room = await Rooms.getInstance().getOrLoadItem(join.id);
      if (room) {
        rooms.push(room);
      }
    }
    this.setState({
      rooms: rooms,
    });
  }

  componentDidMount() {
    super.componentDidMount();
    this.loader.registerObserver(this);
  }

  componentWillUnmount() {
    this.loader.unregisterObserver(this);
    super.componentWillUnmount();
  }

  onItemChanged(item: RoomJoin, change: ListItemChange) {
    this.reload(true);
  }

  private createRoomActions(room: Room): Action[] {
    return [
      new Action("Settings", () => this.showRoomSettings(room)),
      new Action("Delete", () => this.deleteRoom(room)).makeDestructive(),
    ];
  }

  private showRoomSettings(room: Room) {
    SettingsHelper.showDialog(room.settings, settings => {
      Rooms.getInstance().addListItem(room.clone(Room).updateSelf(settings));
    });
  }

  private deleteRoom(room: Room) {
    App.CONTEXT.showTextDialog("Confirm delete", "Are you sure you want to delete this room?", new Action("Yes, I'm sure", () => RoomJoins.getInstance().deleteListItemById(room.id)))
  }

  protected renderContainerContent(): React.ReactElement | null {
    const brandHeaderHeight = 192;
    return <div style={{display: "flex", flexDirection: "column"}}>
      <div style={{display: "flex", flexDirection: "column", height: "100vh"}}>
        <StyledBoxRow
          id="top"
          style={{
            zIndex: 1,
            height: brandHeaderHeight,
            boxSizing: "border-box",
            justifyContent: "center",
          }}>
          {/*<img src={"/images/logotype.svg"} style={{height: "100%", background: "#222", paddingLeft: 12, paddingRight: 12}}/>*/}
          <img src={"/images/logotype.svg"}
               style={{height: brandHeaderHeight, paddingLeft: 12, paddingRight: 12}}/>
          {this.memberAuth.auth?.currentUser
            ? <Card style={{
              position: "absolute",
              top: PD_LG,
              right: PD_LG,
              boxSizing: "border-box",
              width: 48,
              height: 48,
              border: DIVIDER_BLACK,
              flexShrink: 0,
              alignSelf: "center"
            }}>
              <ButtonBase
                onClick={() => BaseApp.CONTEXT.showDialog(
                  null,
                  () => <UserFragment uid={this.memberAuth.auth.currentUser.uid}/>)}
              >
                <img src={UserProfilePhoto(this.memberAuth.member.user)} style={{width: "100%", height: "100%"}}/>
              </ButtonBase>
            </Card>
            : <HeaderButtons actions={[new Action("Login", () => this.props.path.navigate("/login"))]}/>}
        </StyledBoxRow>
        {/*{this.memberAuth.auth?.currentUser*/}
        {/*  ? <StyledBoxRow style={{*/}
        {/*    zIndex: 100,*/}
        {/*    position: "sticky",*/}
        {/*    top: 0,*/}
        {/*    height: 72,*/}
        {/*    boxSizing: "border-box",*/}
        {/*    justifyContent: "center",*/}
        {/*    borderTop: DIVIDER_BLACK,*/}
        {/*    borderBottom: DIVIDER_BLACK,*/}
        {/*    gap: 0,*/}
        {/*    background: COLOR_BG1,*/}
        {/*  }}>*/}
        {/*    <ButtonBase onClick={() => {*/}
        {/*    }} style={{flexBasis: 0, flexGrow: 1, borderRadius: 0, background: BLACK, color: "white"}}>*/}
        {/*      <Typography>Buzzin'</Typography>*/}
        {/*    </ButtonBase>*/}
        {/*    <ButtonBase onClick={() => {*/}
        {/*    }} style={{flexBasis: 0, flexGrow: 1, borderRadius: 0}}>*/}
        {/*      <Typography>People</Typography>*/}
        {/*    </ButtonBase>*/}
        {/*    <ButtonBase onClick={() => {*/}
        {/*    }} style={{flexBasis: 0, flexGrow: 1, borderRadius: 0}}>*/}
        {/*      <Typography>Settings</Typography>*/}
        {/*    </ButtonBase>*/}
        {/*  </StyledBoxRow>*/}
        {/*  : null}*/}
        <StyledBoxColumn style={{flexGrow: 1, alignItems: "center", justifyContent: "center", marginTop: -96}}>
          {this.state.rooms?.length > 0
            ? <StyledBoxColumn style={{boxSizing: "border-box", padding: PD_MD, gap: 4, width: "100%", maxWidth: 480}}>
              <Typography
                style={{color: BLACK, fontWeight: "bold", textAlign: "center", padding: PD_LG}}>
                What's buzzin'
              </Typography>
              {this.state.rooms.map(item => <StyledBoxRow>
                <Button
                  style={{flexGrow: 1}}
                  variant="outlined"
                  endIcon={<ChevronRightOutlined/>}
                  onClick={() => this.props.path.navigate("/r/" + item.id)}>
                  <StyledBoxRow style={{width: "100%"}}>
                    <Typography>{item.id}</Typography>
                  </StyledBoxRow>
                </Button>
                <IconButton
                  onClick={event => App.CONTEXT.showActionsListPopover(event.currentTarget, null, this.createRoomActions(item))}>
                  <MoreVertOutlined/>
                </IconButton>
              </StyledBoxRow>)}
            </StyledBoxColumn>
            : null}
          <CreateOrJoinPage
            path={this.props.path}
            title={!(this.state.rooms?.length > 0) ? "Video calls without the fuss." : null}/>
        </StyledBoxColumn>
      </div>
      {!this.memberAuth.auth?.currentUser
        ?
        FEATURES.map(feature => <FeatureView feature={feature}/>)
        : null}
      <StyledContainer size="xlg"
                       style={{maxWidth: PAGE_FRAGMENT_HALF_WIDTH, width: "100%", margin: "auto", padding: PD_XLG2}}>
        <Typography
          variant="h4"
          style={{
            textAlign: "center",
            padding: PD_XLG,
            marginBottom: -PD_XLG
          }}>
          Get buzzin'
        </Typography>
        <BuzzButton action={new Action("Create or Join", () => window.scrollTo(0, 0))}/>
        <BuzzButton variant="outlined" action={new Action("Login", () => this.props.path.navigate("/login"))}/>
      </StyledContainer>
      <StyledBoxColumn style={{alignItems: "center", padding: PD_XLG2}}>
        <a href={"https://fyne.studio"} target="_blank">
          <img src={"/images/fynestudio.png"} style={{
            width: 96,
            filter: "grayscale(1) brightness(0)",
            opacity: 0.9
          }}/></a>
        <Typography variant="body2" style={{textAlign: "center"}}>
          {`Copyright © ${new Date().getFullYear()} Superfyne LLC. All rights reserved.`}
        </Typography>
      </StyledBoxColumn>
      <img src={"/images/handwave.svg"}
           style={{width: "100%", maxWidth: PAGE_FRAGMENT_WIDTH, margin: "auto"}}/>
    </div>;
  }
}